import { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabase/client";
import { CSRWorkload, AdminCampaign } from "../utils/csrAssignment";

interface CSRUser {
  id: string;
  email: string;
  full_name?: string;
}

export function useAdminCampaigns(): {
  campaigns: AdminCampaign[];
  csrWorkloads: CSRWorkload[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  refreshWorkloads: () => void;
} {
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [csrWorkloads, setCsrWorkloads] = useState<CSRWorkload[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch CSR workloads using the optimized view from database
  const fetchCSRWorkloads = useCallback(async (): Promise<CSRWorkload[]> => {
    try {
      console.log("Fetching CSR workloads...");
      
      // Try to use the optimized view first
      // Try enhanced workload view first
      const { data: enhancedWorkloadData, error: enhancedError } = await supabase
        .from('csr_workload_summary_enhanced')
        .select('*');
      
      if (!enhancedError && enhancedWorkloadData) {
        console.log("Successfully fetched enhanced workloads from optimized view:", enhancedWorkloadData);
        return enhancedWorkloadData.map(row => ({
          csr_id: row.csr_id,
          csr_email: row.csr_email,
          csr_name: row.csr_name,
          assigned_campaigns: row.assigned_campaigns || 0,
          assigned_companies: row.assigned_companies || 0,
          company_campaigns: row.company_campaigns || 0,
          active_lead_responses: row.active_lead_responses || 0,
          total_workload_score: row.total_workload_score || 0,
          csr_joined_date: row.csr_joined_date,
          csr_last_active: row.csr_last_active
        }));
      }
      
      // Fallback to basic workload view
      const { data: workloadData, error: workloadError } = await supabase
        .from('csr_workload_summary')
        .select('*');
      
      if (!workloadError && workloadData) {
        console.log("Successfully fetched workloads from optimized view:", workloadData);
        return workloadData.map(row => ({
          csr_id: row.csr_id,
          csr_email: row.csr_email,
          csr_name: row.csr_name,
          assigned_campaigns: row.assigned_campaigns || 0,
          assigned_companies: 0, // Basic view doesn't have company data
          company_campaigns: 0,
          active_lead_responses: row.active_lead_responses || 0,
          total_workload_score: row.total_workload_score || 0,
          csr_joined_date: row.csr_joined_date,
          csr_last_active: row.csr_last_active
        }));
      }
      
      console.log("Optimized view not available, using fallback calculation");
      
      // Fallback: Calculate workloads manually
      const { data: csrUsers, error: csrError } = await supabase
        .from('users')
        .select('id, email, full_name, created_at, last_sign_in_at')
        .eq('role', 'csr');
      
      if (csrError || !csrUsers) {
        console.error("Error fetching CSR users:", csrError);
        // Return preview data for development with company assignment examples
        return [
          { 
            csr_id: 'csr-preview-1', 
            csr_email: 'csr1@example.com', 
            assigned_campaigns: 2, 
            assigned_companies: 1, 
            company_campaigns: 4, 
            active_lead_responses: 5, 
            total_workload_score: 16 // (1*5) + (2*3) + 5
          },
          { 
            csr_id: 'csr-preview-2', 
            csr_email: 'csr2@example.com', 
            assigned_campaigns: 3, 
            assigned_companies: 0, 
            company_campaigns: 0, 
            active_lead_responses: 3, 
            total_workload_score: 12 // (0*5) + (3*3) + 3
          },
          { 
            csr_id: 'csr-preview-3', 
            csr_email: 'csr3@example.com', 
            assigned_campaigns: 1, 
            assigned_companies: 2, 
            company_campaigns: 6, 
            active_lead_responses: 2, 
            total_workload_score: 15 // (2*5) + (1*3) + 2
          }
        ];
      }
      
      // Calculate workloads for each CSR
      const workloads = await Promise.all(
        csrUsers.map(async (csr) => {
          // Count assigned campaigns
          const { count: campaignCount } = await supabase
            .from('campaigns')
            .select('*', { count: 'exact', head: true })
            .eq('assigned_csr', csr.id);
          
          // Count active lead responses
          const { count: leadResponseCount } = await supabase
            .from('lead_responses')
            .select('*', { count: 'exact', head: true })
            .eq('assigned_csr', csr.id)
            .in('status', ['new', 'read', 'reply_requested']);
          
          const assigned_campaigns = campaignCount || 0;
          const active_lead_responses = leadResponseCount || 0;
          const total_workload_score = (assigned_campaigns * 3) + active_lead_responses;
          
          return {
            csr_id: csr.id,
            csr_email: csr.email,
            csr_name: csr.full_name,
            assigned_campaigns,
            active_lead_responses,
            total_workload_score,
            csr_joined_date: csr.created_at,
            csr_last_active: csr.last_sign_in_at
          };
        })
      );
      
      console.log("Calculated CSR workloads:", workloads);
      return workloads;
      
    } catch (err) {
      console.error("Error in fetchCSRWorkloads:", err);
      // Return preview data on error
      return [
        { csr_id: 'csr-preview-1', csr_email: 'csr1@example.com', assigned_campaigns: 2, active_lead_responses: 5, total_workload_score: 11 },
        { csr_id: 'csr-preview-2', csr_email: 'csr2@example.com', assigned_campaigns: 1, active_lead_responses: 3, total_workload_score: 6 },
        { csr_id: 'csr-preview-3', csr_email: 'csr3@example.com', assigned_campaigns: 3, active_lead_responses: 2, total_workload_score: 11 }
      ];
    }
  }, []);

  // Separate function to fetch campaign data
  const fetchCampaignsData = useCallback(async (): Promise<AdminCampaign[]> => {
    console.log("Attempting to fetch campaigns...");
    
    // First try: Basic query
    const { data: campaignData1, error: campaignError1 } = await supabase
      .from("campaigns")
      .select("*")
      .order('created_at', { ascending: false });

    let campaignData = campaignData1;

    if (campaignError1) {
      console.error("Basic campaign query failed:", campaignError1);
      
      // Second try: Query with explicit columns
      const { data: campaignData2, error: campaignError2 } = await supabase
        .from("campaigns")
        .select("id, name, user_id, created_at, status, assigned_csr")
        .order('created_at', { ascending: false });
        
      if (campaignError2) {
        console.error("Explicit column query failed:", campaignError2);
        throw campaignError2;
      }
      
      campaignData = campaignData2;
    }

    console.log("Fetched campaigns:", campaignData);
    console.log("Campaign count:", campaignData?.length || 0);

    // If still no campaigns, let's try to understand why
    if (!campaignData || campaignData.length === 0) {
      console.log("No campaigns found. Checking if table is accessible...");
      
      // Try to count campaigns
      const { count, error: countError } = await supabase
        .from("campaigns")
        .select("*", { count: 'exact', head: true });
        
      if (countError) {
        console.error("Count query failed:", countError);
      } else {
        console.log("Total campaigns in table:", count);
      }
    }

    // For now, skip user email fetching to avoid permission issues
    // We'll show campaigns with simplified user identification
    console.log("Skipping user email fetch to avoid permission issues");
    
    const uniqueUserIds = [...new Set(campaignData?.map(c => c.user_id) || [])];
    const userEmails = uniqueUserIds.reduce((acc: { [key: string]: string }, userId: string) => {
      acc[userId] = `User-${userId.substring(0, 8)}`;
      return acc;
    }, {});
    console.log("Using simplified user mapping:", userEmails);

    // Fetch user-company associations to get company names
    const userCompanyMap: { [key: string]: { company_id: string, company_name: string } } = {};
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, company_id')
        .in('id', uniqueUserIds);

      if (!userError && userData) {
        // Get unique company IDs
        const companyIds = [...new Set(userData.map(u => u.company_id).filter(id => id != null))];
        
        if (companyIds.length > 0) {
          const { data: companyData, error: companyError } = await supabase
            .from('companies')
            .select('id, name')
            .in('id', companyIds);

          if (!companyError && companyData) {
            // Create company map
            const companyMap = new Map(companyData.map(c => [c.id, c.name]));
            
            // Map users to their companies
            userData.forEach(user => {
              if (user.company_id) {
                userCompanyMap[user.id] = {
                  company_id: user.company_id,
                  company_name: companyMap.get(user.company_id) || 'Unknown Company'
                };
              }
            });
          }
        }
      }
    } catch (err) {
      console.log("Could not fetch company data:", err);
    }

    // Get CSR users to enhance campaign data
    let csrUsers = [];
    try {
      const { data: csrData, error: csrError } = await supabase
        .rpc('get_users_by_role', { user_role: 'csr' });
      
      if (!csrError && csrData) {
        csrUsers = csrData;
        console.log("Fetched CSR users:", csrUsers);
      }
    } catch (err) {
      console.log("RPC function not available for CSR users:", err);
    }
    
    // If RPC failed or returned no data, show preview data
    if (csrUsers.length === 0) {
      csrUsers = [
        { id: 'csr-preview-1', email: 'csr1@example.com' },
        { id: 'csr-preview-2', email: 'csr2@example.com' }
      ];
      console.log("Using preview CSR users:", csrUsers);
    }

    // Enhance campaigns with CSR assignment info, user email, and company info
    const enhancedCampaigns = campaignData?.map(campaign => {
      const assignedCSR = csrUsers.find((csr: CSRUser) => csr.id === campaign.assigned_csr);
      const companyInfo = userCompanyMap[campaign.user_id];
      return {
        ...campaign,
        assigned_csr_email: assignedCSR?.email,
        user_email: userEmails[campaign.user_id] || 'Unknown',
        company_id: companyInfo?.company_id || null,
        company_name: companyInfo?.company_name || null
      };
    }) || [];

    console.log("Enhanced campaigns:", enhancedCampaigns);
    return enhancedCampaigns;
  }, []);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch campaigns and workloads in parallel for better performance
      const [campaignData, workloadData] = await Promise.all([
        fetchCampaignsData(),
        fetchCSRWorkloads()
      ]);

      setCampaigns(campaignData);
      setCsrWorkloads(workloadData);
      
    } catch (err) {
      console.error("Error in fetchCampaigns:", err);
      setError(err instanceof Error ? err : new Error('Failed to fetch campaigns and workloads'));
    } finally {
      setLoading(false);
    }
  }, [fetchCSRWorkloads, fetchCampaignsData]);

  // Separate function to refresh only workloads
  const refreshWorkloads = useCallback(async () => {
    try {
      const workloadData = await fetchCSRWorkloads();
      setCsrWorkloads(workloadData);
    } catch (err) {
      console.error("Error refreshing workloads:", err);
    }
  }, [fetchCSRWorkloads]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  return {
    campaigns,
    csrWorkloads,
    loading,
    error,
    refetch: fetchCampaigns,
    refreshWorkloads
  };
}
